import {ApiErrorCode, FieldErrorCode} from "../../api/errors";

// The site's words for the API's error codes (src/api/errors.ts), in English. Plain data with {placeholders} for the
// error's params, filled by format() (src/i18n/format.ts): {name}, {name:bytes} (a size) and {name:list} (choices the
// API joins with |). Another language is a copy of this file. Pages can say something more specific for their
// context (ErrorAlert's messages).
export const errorMessages: Record<ApiErrorCode, string> = {
    bad_request: "That wasn't accepted. Check the values and try again.",
    validation_failed: "Some of the details weren't accepted.",
    unauthorized: 'Your session has ended. Please sign in again.',
    forbidden: "You don't have permission to do that.",
    not_found: "That couldn't be found. It may have been removed.",
    method_not_allowed: "That can't be done here.",
    conflict: 'That conflicts with something that already exists.',
    too_many_requests: "You've done that a lot recently. Please wait a while and try again.",
    unavailable: "That isn't available right now. Please try again later.",
    maintenance: 'The website is currently undergoing maintenance. Please check back later.',
    server_error: 'Something went wrong on our side. Please try again in a moment.',

    username_in_use: 'That username is already in use.',
    email_in_use: 'That email address is already in use by another account.',
    incorrect_password: 'The current password is incorrect.',
    reauthentication_required: 'Please enter your password to continue.',
    session_ended: 'For your security, you were signed out. Please sign in again.',
    account_disabled: 'This account is disabled.',
    cannot_delete_self: "You can't delete your own account here.",
    cannot_delete_admin: "The site administrator's account can't be deleted.",
    invalid_code: "That code isn't right, or has expired. Check it and try again.",
    mfa_off: 'Two-factor authentication is off.',
    authenticator_already_set_up: 'An authenticator app is already set up.',
    email_codes_already_on: 'Email codes are already on.',
    upload_expired: 'The upload took too long, or was already used. Please choose the file again.',
    image_rejected: "That image wasn't accepted. It must be a real picture of the right type, at most {maxDimension} pixels on each side.",

    sku_in_use: 'A product with that SKU already exists.',
    version_exists: 'The product already has that version.',
    product_has_licenses: "The product has licenses, so it can't be deleted.",
    current_version: "The current version can't be deleted or delisted. Make another version current first.",
    version_delisted: "A delisted version can't be made current. List it again first.",
    version_has_no_files: "That version has no uploaded files, so it can't be made current.",
    current_version_track: "A current version can't switch between Early Access and released. Make another version current first.",
    last_file_of_current_version: "That's the current version's last file, so it can't be deleted.",
    file_not_uploaded: "The file hasn't been uploaded yet.",
    image_not_uploaded: "The image wasn't uploaded. Please try again.",
    image_in_use: 'The documentation shows that image.',
    no_license: "Downloads are for license holders, and there's no license for this product on your account.",
    product_has_no_images: 'Add some images to the product first.',
    not_a_pdf: "That file isn't a PDF.",

    license_key_in_use: 'A license with that key already exists.',
    not_a_review_license: 'That isn\'t a review license.',
    not_a_press_inquiry: 'Review licenses are issued from press inquiries.',
    review_license_issued: 'This inquiry\'s review license has been issued already.',
    unknown_product: "That product doesn't exist.",

    license_required: "That license can't be used for support. It may have been revoked.",
    ticket_closed: "This ticket can't be replied to or changed any more. It may have been closed, or its license revoked.",
    ticket_already_in_state: 'The ticket already has that status. Reload the page to see the latest.',
    reason_not_allowed: "That reason can't be given here.",
    turnstile_failed: "The check that you're not a bot didn't go through. Please do it again.",

    not_for_sale: "That product isn't on sale right now.",
    shop_closed: "We're not taking orders right now. Please check back soon.",
    provider_unavailable: "That way of paying isn't available right now. Please try another.",
    address_not_taxable: "We couldn't work out the tax for that address. Check your country and postal code.",
    campaign_sent: 'This email has already been sent, so it can no longer be changed.',
    test_required: 'Send yourself a test of this email first (again, after any change).'
};

// What's wrong with one field, shown under it (or after its label in an alert).
export const fieldErrorMessages: Record<FieldErrorCode, string> = {
    required: 'This is required.',
    invalid: "This isn't valid.",
    invalid_format: "This isn't in the right format.",
    too_long: 'This must be at most {max} characters.',
    too_large: 'This must be at most {max:bytes}.',
    out_of_range: 'This is out of range.',
    not_allowed: 'This must be one of: {allowed:list}.',
    duplicate: 'This is a duplicate.',
    conflict: "This can't go with the other choices made.",
    not_early_access_user: 'This must be an Early Access User.',
    dev_version: "That's a CI or development build's version (-dev or -dev.N), which customers can't activate."
};

// The API's field names, as an alert lists them. Pages that show a field's error under the field don't need these.
export const fieldLabels: { [field: string]: string } = {
    username: 'Username',
    emailAddress: 'Email address',
    displayName: 'Display name',
    password: 'Current password',
    newPassword: 'New password',
    code: 'Code',
    status: 'Status',
    name: 'Name',
    sku: 'SKU',
    keyPrefix: 'Key prefix',
    prices: 'Prices',
    edition: 'Edition',
    amount: 'Amount',
    currency: 'Currency',
    version: 'Version',
    releasedOn: 'Release date',
    title: 'Title',
    operatingSystem: 'Operating system',
    fileName: 'File name',
    contentType: 'File type',
    size: 'File size',
    content: 'Documentation',
    productId: 'Product',
    licensee: 'Licensed to',
    subject: 'Subject',
    body: 'Message',
    attachments: 'Attachments',
    state: 'Status',
    reasonId: 'Reason',
    provider: 'Payment method',
    country: 'Country',
    postalCode: 'Postal code',
    requirements: 'System requirements',
    platforms: 'Platform',
    platform: 'Platform',
    minimumVersion: 'Minimum version',
    architectures: 'Processors',
    formats: 'Formats',
    devices: 'Devices',
    storeUrl: 'Store link',
    processor: 'Processor',
    memory: 'Memory',
    disk: 'Disk space',
    other: 'Also needs',
    notAvailable: 'Also not available'
};

// "Price 2", "Attachment 1": a field in a list (prices[1].amount is the 2nd price's amount).
export const listItemLabel = '{label} {number}';

export const errorReference = 'Reference: {requestId}';

// When the request didn't reach the API at all.
export const unreachable = "Couldn't reach the server. Check your internet connection and try again.";

// When every field the API refused is shown under the field itself.
export const checkFieldsBelow = 'Check the highlighted fields below.';
